/**
 * Netlify Image CDN URL helpers.
 *
 * Netlify's dedicated image endpoint keeps remote transforms and caching off
 * the general Node server function invocation budget.
 *
 * Plain Next development does not expose Netlify's image endpoint, so local
 * development serves originals by default. NEXT_PUBLIC_OPTIMIZED_IMAGES can
 * explicitly enable or disable optimization in any environment.
 */

const imageOptimizationSetting = process.env.NEXT_PUBLIC_OPTIMIZED_IMAGES;

export const IMAGES_OPTIMIZED =
  imageOptimizationSetting === "true" ||
  (imageOptimizationSetting !== "false" && process.env.NODE_ENV !== "development");
export const NETLIFY_IMAGE_PATH = "/.netlify/images";

const DEFAULT_QUALITY = 75;

export type ResponsiveImageCandidate = {
  src: string;
  width: number;
};

type ResponsiveImageOptions = {
  optimize?: boolean;
  preferredWidth?: number;
  quality?: number;
};

export const optimizedImageUrl = (
  src: string,
  width: number,
  quality = DEFAULT_QUALITY
): string => {
  // Only rewrite absolute remote URLs. Local assets (/pro-1.jpg), data URIs,
  // and already-optimized URLs pass through.
  if (!IMAGES_OPTIMIZED || !src?.startsWith("http") || src.includes(NETLIFY_IMAGE_PATH)) {
    return src;
  }
  return `${NETLIFY_IMAGE_PATH}?url=${encodeURIComponent(src)}&w=${width}&q=${quality}`;
};

/**
 * Build a matching src/srcSet pair once, so components do not repeat URL
 * transformation and width-descriptor assembly.
 */
export const responsiveImageProps = (
  candidates: readonly ResponsiveImageCandidate[],
  { optimize = true, preferredWidth, quality = DEFAULT_QUALITY }: ResponsiveImageOptions = {}
): { src: string; srcSet?: string } => {
  const validCandidates = candidates.filter(({ src, width }) => Boolean(src) && width > 0);
  if (validCandidates.length === 0) return { src: "" };

  const transformed = validCandidates.map(({ src, width }) => ({
    src: optimize ? optimizedImageUrl(src, width, quality) : src,
    width,
  }));
  const preferred = transformed.find(({ width }) => width === preferredWidth) ?? transformed[0];

  return {
    src: preferred.src,
    srcSet: transformed.map(({ src, width }) => `${src} ${width}w`).join(", "),
  };
};
